import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Moon,
  Star,
  Cloud,
  Heart,
  Sparkles,
  Volume2,
  Clock,
  ArrowRight,
  BookOpen,
  Baby,
  Users
} from "lucide-react";
import LandingNavigation from "@/components/LandingNavigation";
import Footer from "@/components/ui/footer";
import { useSEO } from "@/hooks/useSEO";

const storyThemes = [
  {
    title: "Dreamy Adventures",
    description: "Gentle adventures that wind down into peaceful sleep",
    icon: Cloud,
    color: "from-blue-400 to-purple-400"
  },
  {
    title: "Sleepy Animals",
    description: "Cuddly creatures getting ready for bed",
    icon: Baby,
    color: "from-pink-400 to-rose-400"
  },
  {
    title: "Magical Nights",
    description: "Soft magic and wonder under the stars",
    icon: Star,
    color: "from-purple-400 to-indigo-400"
  },
  {
    title: "Cozy Moments",
    description: "Warm, comforting stories about home and family",
    icon: Heart,
    color: "from-rose-400 to-orange-400"
  }
];

const benefits = [
  {
    icon: Clock,
    title: "Perfect Length",
    description: "Stories timed just right for bedtime - not too long, not too short"
  },
  {
    icon: Moon,
    title: "Calming Content",
    description: "No exciting endings or cliffhangers - just peaceful conclusions"
  },
  {
    icon: Volume2,
    title: "Soothing Narration",
    description: "Gentle voice options designed to relax and calm"
  },
  {
    icon: Sparkles,
    title: "Sleep-Inducing Themes",
    description: "Stories crafted to help children transition to dreamland"
  }
];

const sampleStories = [
  {
    title: "The Sleepy Little Star",
    description: "A twinkling star learns it's time to rest",
    ageRange: "3-5",
    duration: "5 min"
  },
  {
    title: "Bunny's Cozy Burrow",
    description: "A little bunny prepares for a peaceful night",
    ageRange: "2-4",
    duration: "4 min"
  },
  {
    title: "The Quiet Forest",
    description: "Forest animals settle down as night falls",
    ageRange: "4-6",
    duration: "6 min"
  },
  {
    title: "Dreams of the Moon",
    description: "A gentle journey to the land of dreams",
    ageRange: "3-6",
    duration: "5 min"
  }
];

export default function BedtimeStoriesPage() {
  useSEO({
    title: 'Bedtime Stories for Kids | Calming Sleep Stories | SnuggleCrafters',
    description: 'Discover calming bedtime stories perfect for helping children wind down. AI-personalized tales that create peaceful bedtime routines. Perfect for ages 2-12.',
    keywords: 'bedtime stories, sleep stories for kids, calming stories, nighttime stories, peaceful bedtime, children sleep stories',
    url: '/stories/bedtime',
    type: 'website',
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Stories', url: '/stories' },
      { name: 'Bedtime Stories', url: '/stories/bedtime' }
    ]
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-purple-50 to-white dark:from-gray-900 dark:via-indigo-950 dark:to-gray-900">
      <LandingNavigation />

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <Star
              key={i}
              className="absolute text-yellow-300/30 animate-pulse"
              style={{
                top: `${Math.random() * 60}%`,
                left: `${Math.random() * 100}%`,
                width: `${8 + Math.random() * 8}px`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))}
        </div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 mb-6">
            <Moon className="h-4 w-4" />
            <span className="text-sm font-medium">Perfect for peaceful nights</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            Calming Bedtime Stories for Kids
          </h1>

          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-8">
            Help your child drift off to sleep with gentle, personalized bedtime stories.
            Each tale is crafted to calm, comfort, and create the perfect end to their day.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <Link href="/auth">
              <Button size="lg" className="gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700">
                <Sparkles className="h-5 w-5" />
                Create a Bedtime Story
              </Button>
            </Link>
            <Link href="/features">
              <Button size="lg" variant="outline" className="gap-2">
                <BookOpen className="h-5 w-5" />
                See How It Works
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Story Themes */}
      <section className="py-16 px-4 bg-white/50 dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Bedtime Story Themes</h2>
            <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Choose from a variety of calming themes designed for peaceful sleep.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {storyThemes.map((theme, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer">
                <CardContent className="pt-6 text-center">
                  <div className={`h-14 w-14 rounded-2xl bg-gradient-to-br ${theme.color} flex items-center justify-center mx-auto mb-4`}>
                    <theme.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{theme.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">{theme.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Why Bedtime Stories Help Sleep</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-8">
                Research shows that consistent bedtime routines help children fall asleep faster and sleep better.
                Our AI-crafted stories are specifically designed to support healthy sleep patterns.
              </p>

              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{benefit.title}</h3>
                      <p className="text-gray-600 dark:text-gray-300 text-sm">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-3xl p-8">
              <div className="text-center mb-6">
                <Moon className="h-16 w-16 mx-auto text-indigo-600 mb-4" />
                <h3 className="text-2xl font-bold">Sample Bedtime Stories</h3>
              </div>
              <div className="space-y-3">
                {sampleStories.map((story, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold">{story.title}</h4>
                      <Badge variant="secondary" className="text-xs">{story.ageRange}</Badge>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{story.description}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                      <Clock className="h-3 w-3" />
                      {story.duration}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Age Groups */}
      <section className="py-16 px-4 bg-white/50 dark:bg-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Stories for Every Age</h2>
            <p className="text-gray-600 dark:text-gray-300">
              Our AI adjusts story complexity and themes based on your child's age.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Link href="/stories/age-3-5">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer h-full">
                <CardHeader>
                  <Badge className="w-fit bg-pink-100 text-pink-700 hover:bg-pink-100">Ages 3-5</Badge>
                  <CardTitle className="text-xl">Toddler Bedtime Tales</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Simple, short stories with familiar characters and gentle repetition.
                  </p>
                  <span className="text-purple-600 font-medium flex items-center gap-1">
                    Explore <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link href="/stories/age-6-8">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer h-full">
                <CardHeader>
                  <Badge className="w-fit bg-blue-100 text-blue-700 hover:bg-blue-100">Ages 6-8</Badge>
                  <CardTitle className="text-xl">Growing Reader Stories</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Longer stories with more narrative and gentle adventure.
                  </p>
                  <span className="text-purple-600 font-medium flex items-center gap-1">
                    Explore <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>

            <Link href="/stories/educational">
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer h-full">
                <CardHeader>
                  <Badge className="w-fit bg-green-100 text-green-700 hover:bg-green-100">All Ages</Badge>
                  <CardTitle className="text-xl">Educational Bedtime</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    Stories that teach while they comfort and calm.
                  </p>
                  <span className="text-purple-600 font-medium flex items-center gap-1">
                    Explore <ArrowRight className="h-4 w-4" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Start Tonight's Bedtime Story</h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Create a personalized, calming bedtime story for your child in just seconds.
          </p>
          <Link href="/auth">
            <Button size="lg" className="gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700">
              <Moon className="h-5 w-5" />
              Create Your First Story Free
              <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
